// Imperative particle burst — sprays N sparkle emoji outward from the centre
// of a source element, then self-removes. Follows the flyStar pattern exactly:
// fixed-position transient nodes, Web Animations API, reduced-motion guard.

const SPARKLE_EMOJIS = ['✨', '💫', '⭐', '🌟'] as const;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
  } catch {
    return false;
  }
}

export interface SpawnSparklesOptions {
  count?: number;
  durationMs?: number;
}

export function spawnSparkles(source: Element, options: SpawnSparklesOptions = {}): void {
  if (prefersReducedMotion()) return;

  const { count = 8, durationMs = 600 } = options;
  const rect = source.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return;

  const originX = rect.left + rect.width / 2;
  const originY = rect.top + rect.height / 2;

  for (let idx = 0; idx < count; idx++) {
    const emoji = SPARKLE_EMOJIS[idx % SPARKLE_EMOJIS.length] ?? '✨';
    const node = document.createElement('span');
    node.textContent = emoji;
    node.setAttribute('aria-hidden', 'true');
    node.style.cssText = `
      position: fixed;
      left: ${originX}px;
      top: ${originY}px;
      transform: translate(-50%, -50%);
      pointer-events: none;
      font-size: 1.5rem;
      line-height: 1;
      user-select: none;
    `;
    document.body.appendChild(node);

    const angle = Math.random() * 2 * Math.PI;
    const radius = 60 + Math.random() * 60;
    const dx = Math.cos(angle) * radius;
    const dy = Math.sin(angle) * radius;

    const animation = node.animate(
      [
        { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
        {
          transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0)`,
          opacity: 0,
        },
      ],
      {
        duration: durationMs,
        easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        fill: 'forwards',
      },
    );

    const cleanup = () => {
      if (node.isConnected) node.remove();
    };

    if (animation && typeof animation.finished?.then === 'function') {
      animation.finished.then(cleanup, cleanup);
    } else if (animation) {
      animation.onfinish = cleanup;
    } else {
      cleanup();
    }
  }
}
